import React from 'react';
import { Time } from '../../shared/types/time';
import { BareSailing, BareSimpleSailing, Sailing, SimpleSailing } from '../../shared/types/sailing';
import { GridEventStyle } from './GridEvent';
import { range } from '../../shared/lib/functional';
import {
  timeToString,
  minutesToTime,
  timeToMinutes,
  isoDate,
  getLocaleUTCDateString,
  calculateTimeDifference,
} from '../../shared/lib/time';
import { getVenueConflicts } from '../lib/api/itinerary';
import { Tooltip } from '@spark-labs/avocado-ui';
import { getDayLocation } from '@go-shared/lib/sailing';
import { makeVariantFromStatus } from '@go-client/pages/GroupsHome';
import { VenueTemplate } from '@go-shared/types/venueTemplate';
import { evaluateMergedVars } from '@go-shared/lib/mergedVars';
import { MergedVariables } from '@go-shared/types/mergedVars';
import { getBasicTimeString } from '@go-shared/lib/event';
import { predictEvents } from '@go-client/lib/api/eventPrediction';
import { EventPredictions, EventPrediction } from '@go-shared/types/eventPrediction';
import GroupBookingPredictionLegend from './GroupBookingPredictionLegend';
import { HourFormat } from '@go-shared/types/brand';
import { BufferTime } from '@go-shared/types/zod/groupEvent';
import './GroupBookingsGrid.scss';
import { CSSPropertiesWithVars } from './GridGroupEvent';

/**
 *
 * TODO: REFACTOR!!!
 *
 */

const MG_TIME_WIDTH = 60;
const MG_DAY_WIDTH = 80;
const MG_HEADING_HEIGHT = 40;
const MG_ROW_HEIGHT = 30;
const MG_MINUTES_IN_DAY = 24 * 60;
const MG_BLOCK_LOCK = 15; // Lock start / drag distance to minutes
const BLOCK_ADJUSTMENT_PX = 15;

export interface GroupBookingsGridEvent {
  startTime: Time;
  duration: number;
  color: string;
  dayIndex: number;
  title?: string;
  mergedVars?: MergedVariables;
  eventPredictionOdds?: number;
  bufferTime?: BufferTime;
}

interface GroupBookingsGridBufferTimeHeights {
  setUpTimeHeight: number;
  breakdownTimeHeight: number;
}

export interface GroupBookingsGridProps {
  days: number;
  blockLength?: number;
  hourFormat: HourFormat;
  venue: VenueTemplate;
  sailing: Sailing | SimpleSailing | BareSailing | BareSimpleSailing;
  languages: string[];
  defaultLanguage: string;
  bufferTime: BufferTime;
  onStartTimeChange?: (time: Time, dayIndex: number) => void;
  onEndTimeChange?: (time: Time) => void;
  groupBookingStartTime?: Time;
  groupBookingEndTime?: Time;
  groupBookingDate?: string;
  locked?: boolean;
  omitted?: string[];
}

export interface GroupBookingsGridState {
  activeId?: string;
  startPosition?: { x: number; y: number };
  events: Array<GroupBookingsGridEvent & GridEventStyle>;
  groupEvents: Array<GroupBookingsGridEvent & GridEventStyle & GroupBookingsGridBufferTimeHeights>;
  bookingHours: Array<GroupBookingsGridEvent & GridEventStyle>;
  newEventLength: number;
  isDragging: boolean;
  startMinutes?: number;
  createdEvent?: { x: number; y: number; w: number; h: number };
  forecast?: Array<GroupBookingsGridEvent & GridEventStyle>;
  bufferSetupHeight?: number;
  bufferBreakdownHeight?: number;
}

function getColorHex(color: string): string {
  let variant = '#d1d1d1';
  switch (color) {
    case 'lime':
      variant = '#a9e6c2';
      break;
    case 'crimson':
      variant = '#f17c7c';
      break;
    case 'banana':
      variant = '#ffcf44';
      break;
    case 'velvet':
      variant = '#9e70b1';
      break;
    case 'smoke':
      variant = '#d1d1d1';
      break;
    default:
      variant = '#d1d1d1';
      break;
  }

  return variant;
}

export default class GroupBookingsGrid extends React.Component<
  GroupBookingsGridProps,
  GroupBookingsGridState
> {
  public readonly state: GroupBookingsGridState = {
    activeId: undefined,
    isDragging: false,
    newEventLength: 0,
    events: [],
    groupEvents: [],
    bookingHours: [],
  };

  private width: number;
  private height: number;
  private blocks: number;
  private blockLength: number;
  private parentDiv: React.RefObject<HTMLDivElement>;
  private lockHeight: number;
  private pixelsPerMinute: number;

  constructor(props: GroupBookingsGridProps) {
    super(props);

    this.blockLength = this.props.blockLength || 60;

    this.blocks = MG_MINUTES_IN_DAY / this.blockLength;
    this.width = props.days * MG_DAY_WIDTH + MG_TIME_WIDTH;
    this.height = this.blocks * MG_ROW_HEIGHT + MG_HEADING_HEIGHT;
    this.pixelsPerMinute = MG_ROW_HEIGHT / this.blockLength;
    this.lockHeight = this.pixelsPerMinute * MG_BLOCK_LOCK;

    this.parentDiv = React.createRef<HTMLDivElement>();

    this.startDragging = this.startDragging.bind(this);
    this.endDragging = this.endDragging.bind(this);
    this.handleMouseMove = this.handleMouseMove.bind(this);
  }

  public async componentDidMount() {
    let conflicts = await getVenueConflicts(this.props.sailing._id, this.props.venue._id);

    let groupEvents: Array<
      GroupBookingsGridEvent & GridEventStyle & GroupBookingsGridBufferTimeHeights
    > = [];
    conflicts.groupEvents.forEach((d, dayIndex) => {
      d.forEach(e => {
        if (e.status === 'cancelled' || (this.props.omitted || []).includes(e.groupRequest)) return;

        groupEvents.push(
          this.eventToStyle({
            title: `[${e.status.toUpperCase()}] ${e.group} - ${e.title} (${getBasicTimeString(
              e,
              this.props.hourFormat
            )})`,
            startTime: e.startTime,
            duration: e.duration,
            color: makeVariantFromStatus(e.status),
            dayIndex,
            bufferTime: e.bufferTime,
          })
        );
      });
    });

    if (
      this.props.groupBookingStartTime &&
      this.props.groupBookingEndTime &&
      this.props.groupBookingDate
    ) {
      const offset = timeToMinutes({ hour: 6, minute: 0 });
      const startMinutes = timeToMinutes(this.props.groupBookingStartTime, offset);
      const start = (startMinutes / 60) * MG_ROW_HEIGHT;
      const duration = timeToMinutes(this.props.groupBookingEndTime, offset) - startMinutes;
      const dayIndex = this.props.sailing.days.findIndex(day => {
        return isoDate(day.date) === this.props.groupBookingDate;
      });

      if (dayIndex === -1) return;

      const bufferSetupHeight = this.props.bufferTime.setUpTime * this.pixelsPerMinute;
      const bufferBreakdownHeight = this.props.bufferTime.breakdownTime * this.pixelsPerMinute;

      this.setState({
        createdEvent: {
          y: start + MG_HEADING_HEIGHT,
          x: MG_TIME_WIDTH + MG_DAY_WIDTH * dayIndex,
          w: MG_DAY_WIDTH,
          h: duration / 2,
        },
        bufferSetupHeight,
        bufferBreakdownHeight,
      });
    }

    let events: Array<GroupBookingsGridEvent & GridEventStyle> = [];
    conflicts.events.forEach((day, dayIndex) => {
      day.forEach(e => {
        events.push(
          this.eventToStyle({
            title: `${e.title} (${getBasicTimeString(e, this.props.hourFormat)})`,
            startTime: e.startTime,
            duration: e.duration,
            color: 'navy',
            dayIndex,
            mergedVars: e.mergedVars,
          })
        );
      });
    });

    let bookingHours: Array<GroupBookingsGridEvent & GridEventStyle> = [];

    this.props.venue.bookingHours
      .filter(bh => bh.status === 'closed')
      .forEach(bh => {
        for (let i = 0; i < this.props.days; i++) {
          bookingHours.push(
            this.eventToStyle({
              title: `Closed`,
              startTime: bh.startTime,
              // If it's closed and the time difference is 0, we set the duration as all day using MG_MINUTES_IN_DAY
              duration: calculateTimeDifference(bh.endTime, bh.startTime) || MG_MINUTES_IN_DAY,
              color: 'navy',
              dayIndex: i,
            })
          );
        }
      });

    let property: string;
    if (typeof this.props.sailing.property === 'string') {
      property = this.props.sailing.property;
    } else {
      property = this.props.sailing.property._id;
    }

    const allPredictions: EventPredictions[] = await predictEvents({
      property,
      venueRequestId: this.props.venue._id,
      days: this.props.days,
    });

    const prediction: EventPredictions[] = this.mergePredictions(allPredictions);
    let roundedOdds: number = 0;

    let forecast: Array<GroupBookingsGridEvent & GridEventStyle> = [];
    prediction.forEach(p => {
      p.prediction.forEach(e => {
        //  Rounds odds to tenths. If less than 10 we dont round them
        roundedOdds = e.odds < 10 ? e.odds : Math.round(e.odds / 10) * 10;
        forecast.push(
          this.eventToStyle({
            startTime: e.event.startTime,
            duration: e.duration,
            color: 'navy',
            dayIndex: p.day - 1,
            title: `[${roundedOdds}% Occupancy prediction] - ${e.event.name}`,
            eventPredictionOdds: roundedOdds,
          })
        );
      });
    });

    this.setState({ events, groupEvents, bookingHours, forecast });
  }

  public render() {
    return (
      <div
        className="relative bg-white select-none"
        onMouseDown={this.startDragging}
        onMouseMove={this.handleMouseMove}
        onMouseUp={this.endDragging}
        ref={this.parentDiv}
        style={{ width: this.width, height: this.height }}
      >
        <GroupBookingPredictionLegend />
        {/* Booking Hours */}
        {this.state.bookingHours.map((e, i) => (
          <div
            key={`b${i}`}
            style={{
              top: e.top,
              left: e.left,
              width: e.width,
              height: e.height,
              zIndex: 1,
            }}
            className="absolute"
          >
            <div className={`w-full h-full`} style={{ backgroundColor: '#3c4650b3' }} />
          </div>
        ))}

        {/* Forecast */}
        {this.state.forecast &&
          this.state.forecast.map((e, i) => (
            <div
              key={`f${i}`}
              style={{
                top: e.top,
                left: e.left + BLOCK_ADJUSTMENT_PX,
                width: e.width - BLOCK_ADJUSTMENT_PX,
                height: e.height,
                zIndex: this.state.activeId === `f${i}` ? 3 : 2,
              }}
              className="absolute border-r border-b border-white"
              onMouseEnter={() => this.setState({ activeId: `f${i}` })}
              onMouseLeave={() => this.setState({ activeId: undefined })}
            >
              <Tooltip text={e.title} className="w-full h-full">
                <div
                  className={`w-full h-full`}
                  style={{
                    backgroundColor: `rgba(251, 56, 139, ${(e.eventPredictionOdds ?? 0) / 100})`,
                  }}
                />
              </Tooltip>
            </div>
          ))}

        {/* Group Events */}
        {this.state.groupEvents.map((e, i) => (
          <div
            key={`g${i}`}
            style={
              {
                top: e.top,
                left: e.left + BLOCK_ADJUSTMENT_PX,
                width: e.width - BLOCK_ADJUSTMENT_PX,
                height: e.height,
                zIndex: this.state.activeId === `g${i}` ? 3 : 2,
                '--buffer-start': `${e.setUpTimeHeight}px`,
                '--buffer-end': `${e.breakdownTimeHeight}px`,
                '--event-duration-end': `${e.height}px`,
                '--backgroundColor': `${getColorHex(e.color)}`,
              } as CSSPropertiesWithVars
            }
            className="absolute border-r border-white group-event"
            onMouseEnter={() => this.setState({ activeId: `g${i}` })}
            onMouseLeave={() => this.setState({ activeId: undefined })}
          >
            <Tooltip text={e.title} className="w-full h-full">
              <div
                className={`w-full h-full bg-${
                  this.state.activeId === `g${i}` ? 'ocean' : e.color
                }`}
              />
            </Tooltip>
          </div>
        ))}

        {/* Events */}
        {this.state.events.map((e, i) => {
          const [title] = evaluateMergedVars(
            e.title || '',
            undefined,
            this.props.languages,
            this.props.defaultLanguage,
            this.props.defaultLanguage,
            e.mergedVars
          );

          return (
            <div
              key={`e${i}`}
              style={{
                top: e.top,
                left: e.left + BLOCK_ADJUSTMENT_PX,
                width: e.width - BLOCK_ADJUSTMENT_PX,
                height: e.height,
                zIndex: this.state.activeId === `e${i}` ? 3 : 2,
              }}
              className="absolute border-r border-b border-white"
              onMouseEnter={() => this.setState({ activeId: `e${i}` })}
              onMouseLeave={() => this.setState({ activeId: undefined })}
            >
              <Tooltip text={title} className="w-full h-full">
                <div
                  className={`w-full h-full bg-${
                    this.state.activeId === `e${i}` ? 'ocean' : e.color
                  }`}
                />
              </Tooltip>
            </div>
          );
        })}

        {/* Created Event */}
        {this.state.createdEvent && (
          <>
            <div
              style={
                {
                  top: this.state.createdEvent.y,
                  left: this.state.createdEvent.x,
                  width: this.state.createdEvent.w - BLOCK_ADJUSTMENT_PX,
                  height: this.state.createdEvent.h,
                  zIndex: 2,
                  '--buffer-start': `${this.state.bufferSetupHeight}px`,
                  '--buffer-end': `${this.state.bufferBreakdownHeight}px`,
                  '--event-duration-end': `${this.state.createdEvent.h}px`,
                  '--backgroundColor': `${getColorHex('velvet')}`,
                } as CSSPropertiesWithVars
              }
              className={`bg-velvet absolute border-r border-white group-event ${
                this.state.bufferBreakdownHeight && this.state.bufferBreakdownHeight > 0
                  ? ''
                  : 'border-b'
              }`}
            >
              <Tooltip text="This Event" className="w-full h-full">
                <div className={`w-full h-full bg-velvet`} />
              </Tooltip>
            </div>
          </>
        )}

        {/* Dragged Event */}
        {this.state.startPosition && this.state.newEventLength !== 0 && (
          <div
            style={{
              top: this.state.startPosition.y,
              left: this.state.startPosition.x,
              width: MG_DAY_WIDTH - BLOCK_ADJUSTMENT_PX,
              height: this.state.newEventLength,
              zIndex: 2,
            }}
            className="bg-velvet absolute border-r border-b border-white"
          />
        )}

        {/* Horizontal bars */}
        {range(this.blocks).map(i => (
          <div
            style={{ top: MG_HEADING_HEIGHT + i * MG_ROW_HEIGHT }}
            className="absolute border-b border-grey-light w-full"
            key={i}
          />
        ))}

        {/* Vertical bars */}
        {range(this.props.days).map(i => (
          <div
            style={{ left: MG_TIME_WIDTH + i * MG_DAY_WIDTH }}
            className="absolute border-l border-grey-light h-full"
            key={i}
          />
        ))}

        {/* Day headings */}
        {this.props.sailing.days.map((sailing, i) => (
          <div
            style={{
              width: MG_DAY_WIDTH,
              height: MG_HEADING_HEIGHT,
              left: MG_TIME_WIDTH + i * MG_DAY_WIDTH,
            }}
            className="absolute text-center bg-orange-light"
            key={i}
          >
            <div
              className="text-xs overflow-hidden whitespace-no-wrap p-1"
              style={{ textOverflow: 'ellipsis' }}
            >
              {getDayLocation(sailing)}
            </div>
            <div
              className="text-xs overflow-hidden whitespace-no-wrap text-teal-darker"
              style={{ textOverflow: 'ellipsis' }}
            >
              {getLocaleUTCDateString(sailing.date)}
            </div>
          </div>
        ))}

        {/* Times bars */}
        {range(this.blocks).map(i => (
          <div
            style={{ width: MG_TIME_WIDTH, top: MG_HEADING_HEIGHT + i * MG_ROW_HEIGHT }}
            className="absolute text-center"
            key={i}
          >
            {timeToString(
              minutesToTime(360 + i * this.blockLength),
              this.props.hourFormat,
              'compact'
            )}
          </div>
        ))}
      </div>
    );
  }

  private bufferTimeToStyle(e: GroupBookingsGridEvent): GroupBookingsGridBufferTimeHeights {
    let setUpTimeHeight = 0;
    let breakdownTimeHeight = 0;
    if (e.bufferTime) {
      const { breakdownTime, setUpTime } = e.bufferTime;
      setUpTimeHeight = (MG_ROW_HEIGHT * setUpTime) / this.blockLength;
      breakdownTimeHeight = (MG_ROW_HEIGHT * breakdownTime) / this.blockLength;
    }

    return { setUpTimeHeight, breakdownTimeHeight };
  }

  private eventToStyle(
    e: GroupBookingsGridEvent
  ): GroupBookingsGridEvent & GridEventStyle & GroupBookingsGridBufferTimeHeights {
    const { setUpTimeHeight, breakdownTimeHeight } = this.bufferTimeToStyle(e);

    return {
      top: MG_HEADING_HEIGHT + (MG_ROW_HEIGHT * timeToMinutes(e.startTime, 360)) / this.blockLength,
      left: MG_TIME_WIDTH + e.dayIndex * MG_DAY_WIDTH,
      width: MG_DAY_WIDTH,
      height: (MG_ROW_HEIGHT * e.duration) / this.blockLength,
      setUpTimeHeight,
      breakdownTimeHeight,
      ...e,
    };
  }

  private startDragging(e: React.MouseEvent<HTMLDivElement>) {
    if (this.props.locked || !this.parentDiv.current) return;

    let bb = this.parentDiv.current.getBoundingClientRect();

    let x = e.clientX - bb.left;
    let y = e.clientY - bb.top;

    if (x <= MG_TIME_WIDTH || y <= MG_HEADING_HEIGHT) return;

    let dayIndex = Math.floor((x - MG_TIME_WIDTH) / MG_DAY_WIDTH);
    x = MG_TIME_WIDTH + dayIndex * MG_DAY_WIDTH;
    let startPosition = Math.floor((y - MG_HEADING_HEIGHT) / this.lockHeight) * this.lockHeight;
    y = MG_HEADING_HEIGHT + startPosition;

    let startMinutes = 360 + startPosition / this.pixelsPerMinute;

    const bufferSetupHeight = this.props.bufferTime.setUpTime * this.pixelsPerMinute;
    const bufferBreakdownHeight = this.props.bufferTime.breakdownTime * this.pixelsPerMinute;

    this.setState({
      isDragging: true,
      startPosition: { x, y },
      startMinutes,
      createdEvent: undefined,
      bufferSetupHeight,
      bufferBreakdownHeight,
    });

    if (this.props.onStartTimeChange) {
      this.props.onStartTimeChange(minutesToTime(startMinutes), dayIndex);
    }
  }

  private endDragging() {
    if (
      this.props.locked ||
      !this.state.isDragging ||
      !this.state.startPosition ||
      !this.parentDiv.current
    ) {
      return;
    }

    let createdEvent = {
      x: this.state.startPosition.x,
      y: this.state.startPosition.y,
      w: MG_DAY_WIDTH,
      h: this.state.newEventLength,
    };

    this.setState({ isDragging: false, startPosition: undefined, newEventLength: 0, createdEvent });
  }

  private handleMouseMove(e: React.MouseEvent<HTMLDivElement>) {
    if (
      this.props.locked ||
      !this.state.isDragging ||
      !this.state.startPosition ||
      this.state.startMinutes === undefined ||
      !this.parentDiv.current
    ) {
      return;
    }

    let bb = this.parentDiv.current.getBoundingClientRect();
    let y = e.clientY - bb.top - this.state.startPosition.y;

    y = Math.floor(y / this.lockHeight) * this.lockHeight;

    let durationMinutes = y / this.pixelsPerMinute;
    let endMinutes = this.state.startMinutes + durationMinutes;

    let endTime = minutesToTime(endMinutes);

    if (y !== this.state.newEventLength) {
      this.setState({ newEventLength: y });
      if (this.props.onEndTimeChange) this.props.onEndTimeChange(endTime);
    }
  }

  private mergePredictions(predictions: EventPredictions[]): EventPredictions[] {
    const merged: EventPredictions[] = [];

    for (let d of predictions) {
      let res: EventPredictions = {
        day: d.day,
        prediction: [],
      };

      let n = d.prediction.length;
      let duration = 0;
      let start: EventPrediction | null = null;

      for (let i = 0; i < n; i++) {
        let current = d.prediction[i];
        let minutes1 = timeToMinutes(current.event.startTime);

        if (start === null) {
          start = { ...current };
          duration = 15;
        } else {
          let minutes2 = timeToMinutes(start.event.startTime) + duration;

          if (minutes1 !== minutes2 || start.odds !== current.odds) {
            start.duration = duration;
            res.prediction.push(start);
            start = { ...current };
            duration = 15;
          } else {
            duration += 15;
          }
        }
      }

      if (start !== null) {
        start.duration = duration;
        res.prediction.push(start);
      }

      merged.push(res);
    }
    return merged;
  }
}
